import React, { memo, useCallback, useState } from 'react';

import { Box, Button, Typography } from '@mui/material';
import { ArrowCheckIcon } from '@sharedCR/coreDesignSystem/icons';
import AutomaticFeeChangeTag from 'app/shared/components/AutomaticFeeChangeTag';
import PropTypes from 'prop-types';

import { IconDirection, IconSize } from 'constants/coreDesignSystem';
import { getPendingChangesAuthorLabel } from 'utils/pendingChangesAuthors';

import {
  SaveSummarySectionTitle,
  SaveSummarySectionVariant,
} from './PendingChangesSaveSummary.constants';
import {
  authorLineStyles,
  automaticFeeTagStyles,
  getDotStyles,
  getSectionStyles,
  listStyles,
  recalculatedToggleStyles,
  summaryWrapperStyles,
} from './PendingChangesSaveSummary.styles';
import { PendingChangesSaveSummaryTestIds } from './PendingChangesSaveSummary.testIds';
import {
  getAlsoChangedLine,
  getAutomaticFeeChangeLine,
  getRecalculatedToggleText,
  getSaveSummaryIntro,
  getStaysPendingLine,
  getWillBeSavedLine,
} from './PendingChangesSaveSummary.utils';

const PendingChangesSaveSummary = ({ summary, isOwnChangesScope }) => {
  const {
    willBeSaved,
    automaticFeeChanges = [],
    recalculatedWillBeSaved = [],
    alsoChangedBySomeoneElse,
    staysPending,
  } = summary;

  const [isRecalculatedExpanded, setIsRecalculatedExpanded] = useState(false);

  const onRecalculatedToggle = useCallback(() => setIsRecalculatedExpanded(prev => !prev), []);

  return (
    <Box sx={summaryWrapperStyles}>
      <Typography variant="regular14">
        {getSaveSummaryIntro({
          isOwnChangesScope,
          savedCount: willBeSaved.length,
          feeChangesCount: automaticFeeChanges.length,
        })}
      </Typography>

      {!!(willBeSaved.length || automaticFeeChanges.length) && (
        <Box sx={getSectionStyles()}>
          <Typography variant="bold12">{SaveSummarySectionTitle.WILL_BE_SAVED}</Typography>
          <Box component="ul" sx={listStyles}>
            {willBeSaved.map(change => (
              <Typography component="li" key={change.fieldKey} variant="regular12">
                {getWillBeSavedLine(change)}
              </Typography>
            ))}
            {automaticFeeChanges.map(feeChange => (
              <Typography
                component="li"
                data-testid={PendingChangesSaveSummaryTestIds.getAutomaticFeeChangeId(
                  feeChange.key,
                )}
                key={feeChange.key}
                variant="regular12"
              >
                {getAutomaticFeeChangeLine(feeChange)}
                <Box component="span" sx={automaticFeeTagStyles}>
                  <AutomaticFeeChangeTag
                    pendingChangesActionType={feeChange.pendingChangesActionType}
                  />
                </Box>
              </Typography>
            ))}
          </Box>
        </Box>
      )}

      {!!recalculatedWillBeSaved.length && (
        <Box sx={getSectionStyles()}>
          <Button
            aria-expanded={isRecalculatedExpanded}
            data-testid={PendingChangesSaveSummaryTestIds.RECALCULATED_TOGGLE}
            sx={recalculatedToggleStyles}
            variant="text"
            onClick={onRecalculatedToggle}
          >
            <ArrowCheckIcon
              direction={isRecalculatedExpanded ? IconDirection.DOWN : IconDirection.RIGHT}
              size={IconSize[14]}
            />
            <Typography variant="medium12">
              {getRecalculatedToggleText(recalculatedWillBeSaved.length)}
            </Typography>
          </Button>
          {isRecalculatedExpanded && (
            <Box
              component="ul"
              data-testid={PendingChangesSaveSummaryTestIds.RECALCULATED_LIST}
              sx={listStyles}
            >
              {recalculatedWillBeSaved.map(change => (
                <Typography component="li" key={change.fieldKey} variant="regular12">
                  {getWillBeSavedLine(change)}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      )}

      {!!alsoChangedBySomeoneElse.length && (
        <Box sx={getSectionStyles(SaveSummarySectionVariant.WARNING)}>
          <Typography variant="bold12">{SaveSummarySectionTitle.ALSO_CHANGED}</Typography>
          <Box component="ul" sx={listStyles}>
            {alsoChangedBySomeoneElse.map(field => (
              <Typography component="li" key={field.fieldKey} variant="regular12">
                <b>{field.propertyName}</b>
                {`: ${getAlsoChangedLine({ otherChanges: field.otherChanges, isOwnChangesScope })}`}
              </Typography>
            ))}
          </Box>
        </Box>
      )}

      {!!staysPending.length && (
        <Box sx={getSectionStyles()}>
          <Typography variant="bold12">{SaveSummarySectionTitle.STAYS_PENDING}</Typography>
          {staysPending.map(author => (
            <Box key={author.key} sx={authorLineStyles}>
              <Box component="span" sx={getDotStyles(author.color)} />
              <Typography variant="regular12">
                {getStaysPendingLine({
                  label: getPendingChangesAuthorLabel(author),
                  changesCount: author.changesCount,
                })}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

PendingChangesSaveSummary.propTypes = {
  summary: PropTypes.shape({
    willBeSaved: PropTypes.arrayOf(PropTypes.object).isRequired,
    alsoChangedBySomeoneElse: PropTypes.arrayOf(PropTypes.object).isRequired,
    staysPending: PropTypes.arrayOf(PropTypes.object).isRequired,
    recalculatedWillBeSaved: PropTypes.arrayOf(PropTypes.object),
    automaticFeeChanges: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        feeName: PropTypes.string,
        pendingChangesActionType: PropTypes.string,
        oldValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        newValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
    ),
  }).isRequired,
  isOwnChangesScope: PropTypes.bool.isRequired,
};

export default memo(PendingChangesSaveSummary);
